import { readFile, realpath, stat } from "node:fs/promises";
import { dirname, join } from "node:path";
import type {
  AgentControllerInterfaceSnapshot,
  RuntimeAgentControllerLaunchContract,
} from "@wakeloop/schemas/types";

// Background report syncs reuse the last successfully probed snapshot for this
// long before paying for another controller probe. Probes cold-start real
// controller processes (codex app-server, opencode serve), so the background
// cadence must stay far cheaper than the 5-minute report sync interval.
const LAST_GOOD_REUSE_TTL_MS = 15 * 60_000;

// A controller's first probe has no last-good snapshot to fall back on, so a
// timeout/spawn-failure freezes it at Error until the user or the next
// background cadence checks again. A single delayed re-probe covers the common
// case where the just-finished setup left the machine too busy for the cold
// controller start to complete in time. This delay gives that machine room to
// settle before the re-probe cold-starts the controller again.
export const AGENT_CONTROLLER_INTERFACE_REPROBE_DELAY_MS = 45_000;

// One-shot re-probe lifecycle for a controller that has never produced a
// completed snapshot. It only decides whether the report-sync scheduler brings
// the next pass forward; it never becomes reported controller status, so
// fail-closed Error reporting is never smoothed by an optimistic state.
//   idle      -> no re-probe pending (also the state after any success)
//   armed     -> first probe failed to complete; request one delayed re-probe
//   attempted -> the single re-probe also failed; no further automatic re-probe
type AgentControllerInterfaceReprobeState = "armed" | "attempted" | "idle";

type AgentControllerInterfaceKeeperEntry = {
  commandFingerprint: AgentControllerCommandFingerprint | null;
  lastGoodSnapshot: AgentControllerInterfaceSnapshot | null;
  lastSuccessAtMs: number;
  reprobe: AgentControllerInterfaceReprobeState;
};

export type AgentControllerCommandFingerprint = {
  mtimeMs: number;
  packageJsonVersion: string | null;
  realPath: string;
  size: number;
};

const keeperEntries = new Map<string, AgentControllerInterfaceKeeperEntry>();

export function buildAgentControllerInterfaceKeeperKey(input: {
  agentId: string;
  commandPath: string | null;
}): string {
  return `${input.agentId}\u0000${input.commandPath ?? ""}`;
}

export function readReusableAgentControllerInterfaceSnapshot(input: {
  commandFingerprint: AgentControllerCommandFingerprint | null;
  keeperKey: string;
  nowMs: number;
}): AgentControllerInterfaceSnapshot | null {
  const entry = keeperEntries.get(input.keeperKey);
  if (!entry?.lastGoodSnapshot) {
    return null;
  }
  if (
    !areAgentControllerCommandFingerprintsEqual(
      entry.commandFingerprint,
      input.commandFingerprint
    )
  ) {
    keeperEntries.delete(input.keeperKey);
    return null;
  }
  if (input.nowMs - entry.lastSuccessAtMs >= LAST_GOOD_REUSE_TTL_MS) {
    return null;
  }
  return entry.lastGoodSnapshot;
}

export function recordAgentControllerInterfaceProbeSuccess(input: {
  commandFingerprint: AgentControllerCommandFingerprint;
  keeperKey: string;
  nowMs: number;
  snapshot: AgentControllerInterfaceSnapshot;
}): void {
  if (input.snapshot.auth.status === "unauthenticated") {
    keeperEntries.delete(input.keeperKey);
    return;
  }
  // A completed probe clears the re-probe lifecycle: the controller now has a
  // last-good snapshot, so it is no longer in the never-succeeded state.
  keeperEntries.set(input.keeperKey, {
    commandFingerprint: input.commandFingerprint,
    lastGoodSnapshot: input.snapshot,
    lastSuccessAtMs: input.nowMs,
    reprobe: "idle",
  });
}

export function recordAgentControllerInterfaceProbeFailure(input: {
  keeperKey: string;
}): void {
  const existingEntry = keeperEntries.get(input.keeperKey);
  if (existingEntry?.lastGoodSnapshot) {
    keeperEntries.delete(input.keeperKey);
    return;
  }
  const entry = keeperEntries.get(input.keeperKey) ?? {
    commandFingerprint: null,
    lastGoodSnapshot: null,
    lastSuccessAtMs: 0,
    reprobe: "idle" as AgentControllerInterfaceReprobeState,
  };
  // Only the never-succeeded (no reusable snapshot) case arms a re-probe, and
  // only from idle. A failure after a reusable snapshot invalidates that
  // snapshot above and waits for the next normal background pass.
  // Retiring the arm is owned by consumeArmedAgentControllerInterfaceReprobe:
  // an entry already past its one shot (attempted) never re-arms here.
  if (entry.lastGoodSnapshot === null && entry.reprobe === "idle") {
    entry.reprobe = "armed";
  }
  keeperEntries.set(input.keeperKey, entry);
}

export async function resolveAgentControllerCommandFingerprint(input: {
  commandPath: string;
  launchContractKind: RuntimeAgentControllerLaunchContract["kind"];
}): Promise<AgentControllerCommandFingerprint | null> {
  try {
    const resolvedPath = await realpath(input.commandPath);
    const commandStat = await stat(resolvedPath);
    return {
      mtimeMs: commandStat.mtimeMs,
      packageJsonVersion:
        input.launchContractKind === "node_script"
          ? await resolveNearestPackageJsonVersion(resolvedPath)
          : null,
      realPath: resolvedPath,
      size: commandStat.size,
    };
  } catch {
    return null;
  }
}

// node_script launch contracts execute an npm package's vendored payload (for
// Codex/Claude Code/OpenCode/OpenClaw: a platform-specific compiled binary
// selected and spawned by the package's bin/*.js entry script) that lives
// alongside -- not inside -- the fingerprinted script file. mtime/size on the
// script alone would miss a payload swap that leaves the entry script's own
// bytes untouched.
//
// This is folded in as a defense-in-depth signal, not the primary one: a real
// `npm install`/`npm update` that bumps the installed version reliably changes
// the entry script's mtime too, because npm re-stamps every file it
// (re-)extracts with the local extraction time rather than preserving any
// original timestamp. Verified empirically (two npm installs of the exact
// same package version 2s apart produced two different mtimes; an npm version
// bump with byte-identical script content across both versions still
// produced a fresh mtime). So the existing mtime/size check already catches
// the common case. The remaining blind spot this does not cover is a
// vendored payload file replaced or corrupted without going through npm at
// all (no version bump recorded in package.json, no mtime change either) --
// closing that would require hashing the platform-specific payload binary
// directly, which this fingerprint intentionally does not do.
const PACKAGE_JSON_SEARCH_DEPTH_LIMIT = 6;

async function resolveNearestPackageJsonVersion(
  scriptRealPath: string
): Promise<string | null> {
  let currentDir = dirname(scriptRealPath);
  for (let depth = 0; depth < PACKAGE_JSON_SEARCH_DEPTH_LIMIT; depth += 1) {
    const version = await readPackageJsonVersion(
      join(currentDir, "package.json")
    );
    if (version !== undefined) {
      return version;
    }
    const parentDir = dirname(currentDir);
    if (parentDir === currentDir) {
      return null;
    }
    currentDir = parentDir;
  }
  return null;
}

// Returns the parsed version string, null when package.json exists but has no
// usable version field (found the package boundary, nothing more to walk up
// for), or undefined when no package.json exists at this level yet (the
// caller keeps walking up toward the package root).
async function readPackageJsonVersion(
  packageJsonPath: string
): Promise<string | null | undefined> {
  let raw: string;
  try {
    raw = await readFile(packageJsonPath, "utf8");
  } catch {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as { version?: unknown };
    return typeof parsed.version === "string" && parsed.version.length > 0
      ? parsed.version
      : null;
  } catch {
    return null;
  }
}

export function didAgentControllerCommandFingerprintRemainStable(input: {
  afterProbe: AgentControllerCommandFingerprint | null;
  beforeProbe: AgentControllerCommandFingerprint | null;
}): boolean {
  return areAgentControllerCommandFingerprintsEqual(
    input.beforeProbe,
    input.afterProbe
  );
}

function areAgentControllerCommandFingerprintsEqual(
  left: AgentControllerCommandFingerprint | null,
  right: AgentControllerCommandFingerprint | null
): boolean {
  return Boolean(
    left &&
      right &&
      left.realPath === right.realPath &&
      left.mtimeMs === right.mtimeMs &&
      left.size === right.size &&
      left.packageJsonVersion === right.packageJsonVersion
  );
}

// Reports whether any controller's first probe armed a re-probe and RETIRES
// those arms (armed -> attempted) in the same call, so the report-sync
// scheduler brings the next pass forward at most once per arm. Retiring at
// observation — rather than waiting for a later probe of that same controller
// to clear it — is what keeps a stuck arm from pinning the sync cadence
// forever: a controller can leave the ready state after arming (a controller
// CLI upgrade/repair nulls its launch contract), after which it is never probed
// again and nothing else would ever clear the arm.
export function consumeArmedAgentControllerInterfaceReprobe(): boolean {
  let armed = false;
  for (const entry of keeperEntries.values()) {
    if (entry.reprobe === "armed") {
      entry.reprobe = "attempted";
      armed = true;
    }
  }
  return armed;
}

// Pure scheduler decision for the next runtime agent controller report sync.
// When a first-probe re-probe is armed, the next pass is brought forward to at
// most the re-probe delay; otherwise it keeps the caller's base cadence. The
// re-probe pass itself runs a real background probe (a still-Error controller
// has no reusable snapshot) and flows through the existing report/refresh path,
// while an already-healthy controller reuses its held snapshot for free.
export function resolveNextRuntimeAgentControllerReportSyncAtMs(input: {
  nowMs: number;
  baseRetryMs: number;
  hasArmedReprobe: boolean;
}): number {
  const baseNextMs = input.nowMs + input.baseRetryMs;
  if (!input.hasArmedReprobe) {
    return baseNextMs;
  }
  return Math.min(
    baseNextMs,
    input.nowMs + AGENT_CONTROLLER_INTERFACE_REPROBE_DELAY_MS
  );
}

export function resetAgentControllerInterfaceKeeperForTests(): void {
  keeperEntries.clear();
}

export function ageAgentControllerInterfaceKeeperForTests(byMs: number): void {
  for (const entry of keeperEntries.values()) {
    entry.lastSuccessAtMs -= byMs;
  }
}
